import { cn } from '@/utils/styles';

interface StepProgressBarProps {
  steps: string[];
  currentStepIndex: number;
  hasError?: boolean;
  isRetestMode?: boolean;
}

const TEAL = '#009E82';
const GRAY = '#CFD0D4';

export const StepProgressBar = ({
  steps,
  currentStepIndex,
  hasError = false,
  isRetestMode = false,
}: StepProgressBarProps) => {
  const lineColor = (lineIndex: number) =>
    lineIndex < currentStepIndex ? TEAL : GRAY;

  const labelColor = (i: number): string => {
    if (i === currentStepIndex && hasError) return 'text-red-500';
    if (i <= currentStepIndex) return 'text-[#146464]';
    return isRetestMode ? 'text-white/40' : 'text-[#CFD0D4]';
  };

  const renderDot = (i: number) => {
    if (i < currentStepIndex) {
      return <div className="w-5 h-5 rounded-full bg-[#009E82]" />;
    }

    if (i === currentStepIndex) {
      const dotBg = hasError ? 'bg-red-500' : 'bg-[#009E82]';
      const pingBg = hasError ? 'bg-red-500/25' : 'bg-[#009E82]/25';
      return (
        <div className="relative flex items-center justify-center w-5 h-5">
          {/* Outer ping ring */}
          <div className={cn('absolute w-5 h-5 rounded-full animate-ping opacity-60', pingBg)} />
          {/* Solid dot */}
          <div className={cn('relative w-5 h-5 rounded-full flex items-center justify-center', dotBg)}>
            {hasError && (
              <span className="text-white text-[9px] font-bold leading-none">!</span>
            )}
          </div>
        </div>
      );
    }

    return <div className="w-3 h-3 rounded-full bg-[#CFD0D4]" />;
  };

  return (
    <div className="w-full">
      {/* Track: dots + lines */}
      <div className="flex items-center">
        {steps.map((_, i) => (
          <div key={i} className="contents">
            {i > 0 && (
              <div className="flex-1 h-0.5" style={{ backgroundColor: lineColor(i - 1) }} />
            )}
            <div className="flex items-center justify-center w-5 h-5">
              {renderDot(i)}
            </div>
          </div>
        ))}
      </div>

      {/* Labels */}
      <div className="flex justify-between mt-3">
        {steps.map((step, i) => (
          <span
            key={i}
            className={cn(
              'text-xs',
              i === 0 ? 'text-left' : i === steps.length - 1 ? 'text-right' : 'text-center',
              'font-normal',
              labelColor(i),
            )}
            style={{ width: i === steps.length - 1 ? 68 : 80 }}
          >
            {step}
          </span>
        ))}
      </div>
    </div>
  );
};
